import { useEffect, useState } from 'react';
import { FunctionComponent } from 'react';
import TopMenu from '../shared/top-menu/top-menu';
import Footer from '../shared/footer/footer';
import './prices.scss';

import {
  LicenseType,
  moduleStructure,
  PSIMAGOPRO_VERSION,
  SpecialDiscountNameDE,
  SpecialDiscountPercentage,
  SPSS_VERSION,
  spssbundles,
  spssmodules,
} from './price-details';
import PricesSKU from './sku';
import OrderForm from './order-form';
import PricesTopBanner from './prices-top-banner';
import H2 from '../shared/html-tags/h2';
import H3 from '../shared/html-tags/h3';
import B from '../shared/html-tags/b';
import P from '../shared/html-tags/p';
export type Props = {};

export const FormatEuro = (value: number) => {
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
};

const OrderSuccess: FunctionComponent<Props> = (props) => {
  const [selectedlicenseType, setSelectedLicenseType] =
    useState<LicenseType>('authorized fixed term');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [calculatedPriceIBM, setCalculatedPriceIBM] = useState<number>(0);
  const [calculatedPricePS, setCalculatedPricePS] = useState<number>(0);
  const [calculatedPriceDiff, setCalculatedPriceDiff] = useState<number>(0);
  const [numberOfLicenses, setNumberOfLicenses] = useState<number>(1);
  const [discountPercentage, setDiscountPercentage] = useState<number>(0);
  const [specialDiscountPercentage, setSpecialDiscountPercentage] = useState<number>(17);
  const [calculatedDiscount, setCalculatedDiscount] = useState<number>(0);
  const [calculatedSpecialDiscount, setCalculatedSpecialDiscount] = useState<number>(0);
  const [selectedBundle, setSelectedBundle] = useState<
    moduleStructure & { includedModules: string[] }
  >(spssbundles[0]);

  const calculatePrice = (licenseType: LicenseType): number => {
    let pricePS = 0;

    //calculate the price for the selected modules and license type
    const bundleVariation = selectedBundle.variations.find((x) => x.lic_type === licenseType);
    if (bundleVariation) {
      pricePS += bundleVariation.PS_Imago_Price;
    }

    selectedModules.forEach((module) => {
      const selectedModule = spssmodules.find((x) => x.name === module);
      if (selectedModule && !selectedBundle.includedModules.includes(selectedModule.name)) {
        const selectedVariation = selectedModule.variations.find((x) => x.lic_type === licenseType);
        if (selectedVariation) {
          pricePS += selectedVariation.PS_Imago_Price;
        }
      }
    });

    return pricePS;
  };

  // ----------------------------------------------------------------
  useEffect(() => {
    let priceIBM = 0;
    let pricePS = 0;
    let discount = 0;

    //calculate the price for the selected modules and license type
    const bundleVariation = selectedBundle.variations.find(
      (x) => x.lic_type === selectedlicenseType
    );
    if (bundleVariation) {
      priceIBM += bundleVariation.IBM_price;
      pricePS += bundleVariation.PS_Imago_Price;
    }

    selectedModules.forEach((module) => {
      const selectedModule = spssmodules.find((x) => x.name === module);
      if (selectedModule && !selectedBundle.includedModules.includes(selectedModule.name)) {
        const selectedVariation = selectedModule.variations.find(
          (x) => x.lic_type === selectedlicenseType
        );
        if (selectedVariation) {
          priceIBM += selectedVariation.IBM_price;
          pricePS += selectedVariation.PS_Imago_Price;
        }
      }
    });

    //calculate the discount
    if (numberOfLicenses <= 1) {
    } else if (numberOfLicenses >= 2 && numberOfLicenses <= 4) {
      discount = 5;
    } else if (numberOfLicenses >= 5 && numberOfLicenses <= 9) {
      discount = 10;
    } else if (numberOfLicenses >= 10 && numberOfLicenses <= 19) {
      discount = 12.5;
    } else if (numberOfLicenses >= 20 && numberOfLicenses <= 49) {
      discount = 15;
    } else if (numberOfLicenses >= 50 && numberOfLicenses <= 99) {
      discount = 17.5;
    } else if (numberOfLicenses >= 100) {
      discount = 20;
    }

    setCalculatedPriceIBM(priceIBM);
    setCalculatedPricePS(pricePS);
    setCalculatedPriceDiff(priceIBM - pricePS);
    setDiscountPercentage(discount);
    setCalculatedDiscount((numberOfLicenses * pricePS * discount) / 100);

    setCalculatedSpecialDiscount(
      (numberOfLicenses * pricePS * (100 - discount) * SpecialDiscountPercentage) / 100 / 100
    );
  }, [
    numberOfLicenses,
    selectedBundle.includedModules,
    selectedBundle.variations,
    selectedModules,
    selectedlicenseType,
    specialDiscountPercentage,
  ]);

  // ----------------------------------------------------------------
  useEffect(() => {
    //When the selected bundle changes, we need to update the selected modules
    setSelectedModules(selectedBundle.includedModules);
  }, [selectedBundle]);

  //If additional modules are selected, then check if there is a bundle that includes all of them. If so, select that bundle.
  useEffect(() => {
    const matchingBundle = spssbundles.find((bundle) =>
      selectedModules.every((module) => bundle.includedModules.includes(module))
    );
    if (matchingBundle && matchingBundle.includedModules.length === selectedModules.length) {
      setSelectedBundle(matchingBundle);
    }
  }, [selectedModules]);

  // ----------------------------------------------------------------
  // ----------------------------------------------------------------
  // ----------------------------------------------------------------
  // ----------------------------------------------------------------
  // ----------------------------------------------------------------

  return (
    <div className="page prices">
      <TopMenu selected="prices"></TopMenu>
      <PricesTopBanner />

      <div className="page-content bg-1">
        <div className="inner-content">
          {/* == Unser Angebot ================================================================================= */}
          <div className="cost-summary">
            <H2 language="de">Unser Angebot für dich:</H2>
            <b>PS IMAGO PRO {PSIMAGOPRO_VERSION}</b> {' - '}
            {selectedlicenseType === 'authorized fixed term' && (
              <>
                <b style={{ color: 'var(--PriceDekoColor)' }}>
                  Mietlizenz für {numberOfLicenses} Authorized User
                </b>{' '}
                für 12-Monate
              </>
            )}
            {selectedlicenseType === 'concurrent fixed term' && (
              <>
                <b style={{ color: 'var(--PriceDekoColor)' }}>
                  Mietlizenz für {numberOfLicenses} Concurrent User für 12 Monate
                </b>{' '}
              </>
            )}
            {selectedlicenseType === 'authorized perpetual' && (
              <>
                <b style={{ color: 'var(--PriceDekoColor)' }}>
                  Kauflizenz für {numberOfLicenses} Authorized User
                </b>
                , inkl. 12 Monate Support & Maintenance
              </>
            )}
            {selectedlicenseType === 'concurrent perpetual' && (
              <>
                <b style={{ color: 'var(--PriceDekoColor)' }}>
                  Kauflizenz für {numberOfLicenses} Concurrent User
                </b>
                , inkl. 12 Monate Support & Maintenance
              </>
            )}{' '}
            - inkl. der folgenden Module:
            <ul>
              {selectedModules.map((module) => (
                <li key={module}>
                  <b>
                    {module} {module === 'IBM SPSS Base Statistics' && SPSS_VERSION}
                  </b>{' '}
                  -{' '}
                  <span style={{ fontSize: '0.7rem' }}>
                    {
                      spssmodules
                        .find((x) => x.name === module)
                        ?.variations.find((x) => x.lic_type === selectedlicenseType)?.sku
                    }
                  </span>
                </li>
              ))}
            </ul>
            <P language="de">
              Gesamtkosten:{' '}
              {(selectedlicenseType === 'authorized perpetual' ||
                selectedlicenseType === 'concurrent perpetual') && <> Einmalig </>}
              <b style={{ color: 'var(--PriceDekoColor)' }}>
                {FormatEuro(
                  numberOfLicenses * calculatedPricePS -
                    calculatedDiscount -
                    calculatedSpecialDiscount
                )}
              </b>{' '}
              {(selectedlicenseType === 'authorized fixed term' ||
                selectedlicenseType === 'concurrent fixed term') && <> pro Jahr </>}
              zzgl. ges. USt.
            </P>
            {/* 
            <P language="de">
              Gegenüber dem regulären IBM SPSS Statistics Preis von{' '}
              {FormatEuro(numberOfLicenses * calculatedPriceIBM)} sparst Du{' '}
              {FormatEuro(
                numberOfLicenses * calculatedPriceIBM -
                  (numberOfLicenses * calculatedPricePS -
                    calculatedDiscount -
                    calculatedSpecialDiscount)
              )}
              , obwohl Du mit PS IMAGO PRO einen deutlich größeren Funktionsumfang bekommst als mit
              IBM SPSS Statistics alleine.
            </P>
            <br />
              */}
            <div style={{ display: 'flex' }}>
              <div className="confirmation">
                <div className="confirmation-text">
                  <h4 style={{ color: 'var(--PriceDekoColor)', fontSize: '1.5rem' }}>
                    Vielen Dank für Dein Interesse an PS IMAGO PRO.
                  </h4>
                  <p style={{ color: 'var(--PriceDekoColor)', fontSize: '1.25rem' }}>
                    Wir setzen uns kurzfristig mit dir in Verbindung, um sicherzustellen, daß mit
                    der Bestellung alles in Ordnung ist.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* =================================================================================== */}
      <div className="page-content bg-2">
        <div className="inner-content" style={{ paddingTop: '0' }}>
          <H2 language="de">Wo ist der Haken?</H2>
          <ul>
            <li>
              <P language="de">Es gibt keinen.</P>
            </li>
            <li>
              <P language="de">
                PS IMAGO PRO findest Du im offiziellen IBM Produktkatalog mit dem folgenden Link:{' '}
                <a
                  href="https://www.ibm.com/partnerplus/directory/solution/0054"
                  target="_blank"
                  rel="noreferrer"
                >
                  IBM Partner Portal
                </a>
              </P>
            </li>
          </ul>

          <H2 language="de">Gut zu wissen:</H2>
          <ul>
            <li>
              <P language="de">
                PS IMAGO PRO enthält immer auch die SPSS Statistics Lizenzen, welche Du bei der
                Bestellung ausgewählt hast. Module kannst Du auch später hinzufügen, solange Du die
                Lizenz mietest, oder Du die jährliche Support & Maintenance Vereinbarung gebucht
                hast. Eine bestehende SPSS Statistics Lizenz kannst Du selbstverständlich weiter
                benutzen oder gegebenenfalls weiter verkaufen.
              </P>
            </li>
            <li>
              <P language="de">
                Die Software ist nur für Windows verfügbar. Eine Mac oder Linux Version gibt es
                nicht.
              </P>
            </li>

            <li>
              <P language="de">
                PS IMAGO PRO kannst Du weltweit in den meisten Ländern kaufen. Unser Angebot von 2x4
                besteht für folgende Länder: Deutschland, Österreich, Schweiz, Liechtenstein,
                Norwegen, Schweden, Dänemark, Island.
                <br /> In anderen Ländern kann es sein, dass die Preise abweichen. Gerne
                unterstützen wir Dich dabei, dort den richtigen Predictive Solutions Partner zu
                finden.
              </P>
            </li>

            <li>
              <P language="de">
                Lizenzen für Authorized User sind an eine bestimmte Person gebunden und können auf
                einem PC installiert werden. Das ist übrigens bei IBM genau so, auch wenn es früher
                einmal eine 2. Installationsmöglichkeit auf einem Laptop gegeben hat. Bei einem
                Wechsel des Computers oder Mitarbeiters senden wir Dir aber natürlich kostenlos
                einen neuen Lizenzcode für die Installation zu.
                <br />
                Lizenzen für Concurrent User sind an eine bestimmte Anzahl von gleichzeitigen
                Benutzungen gebunden. Das bedeutet, dass Du so viele Lizenzen benötigst, wie Du
                gleichzeitig Benutzer haben möchtest. Die Software kann auf beliebig vielen
                Computern installiert werden, aber nur so viele Benutzer können gleichzeitig
                arbeiten, wie Lizenzen gekauft oder gemietet wurden. Für die Verwendung von
                Concurrent Usern wird ein Lizenzserver installiert, der sich um die Verwaltung der
                gleichzeitigen Zugriffe kümmert. Kontaktiere uns einfach, wenn Du zur Lizenzierung
                Fragen hast.
              </P>
            </li>

            <li>
              <P language="de">
                Beim Kauf von PS IMAGO PRO sind bereits 12 Monate Support & Maintenance inklusive.
                In dieser Zeit erhälst Du von uns automatische Updates und wir stehen Dir für
                technischen Support zur Verfügung. Nach Ablauf der 12 Monate kannst Du entscheiden,
                ob Du weitere 12 Monate für 25% des Kaufpreises buchen möchtest (+ 3% Steigerung pro
                Jahr). Wenn Du PS IMAGO PRO mietest, ist Support & Maintenance bereits in der
                Jahresmiete enthalten und es kommen keine weiteren Kosten auf Dich zu.
              </P>
            </li>

            <li>
              <P language="de">
                Du erhältst Updates und Support zu PS IMAGO PRO und SPSS Statistics direkt von 2x4,
                solange Du die Software mietest, oder es eine aktive Support & Maintenance
                Vereinbarung mit 2x4 gibt. Sende uns einfach eine E-Mail an{' '}
                <a href="mailto:support@2x4.de?subject=Ich brauche Hilfe bei PS IMAGO PRO">
                  support@2x4.de
                </a>{' '}
                oder benutze unser <a href="/contact">Kontaktformular</a>
              </P>
            </li>
          </ul>
        </div>
      </div>

      <Footer></Footer>
    </div>
  );
};

export default OrderSuccess;
